import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { systemConfig } from '@/config/systemConfig';
import InspectionStatus from '@/enums/InspectionStatus.enum';
import { startLocationEvidenceUpload } from '@/serviceworker/mediator';
import { upsertInspection } from '@/store/slices/inspectionsSlice';
import store from '@/store/store';
import { locationVerificationDraftStorage } from '@/store/idbStorage/locationVerificationDraftStorage';
import { factoryVisitSessionStorage } from '@/store/idbStorage/factoryVisitSessionStorage';
import { Inspection } from '@/types/Inspection.type';
import { FactoryVisitSession, LocationVerificationDraft } from '@/types/LocationVerification.type';
import { clivApiRequest } from '@/utils/apiClient';
import {
  logFactoryVerificationDebug,
  logFactoryVerificationError,
  summarizeDraft,
  summarizeLocationEvidence,
} from '@/utils/factoryVerificationLogger.util';
import {
  getStatusForStart,
  resolveWriteOnceStartTime,
  withWriteOnceStartTime,
} from '@/utils/inspectionStartFlow.utils';
import {
  buildAutoReuseDraft,
  getPrimaryEntranceCoordinates,
  toServerLocationEvidence,
} from '@/utils/locationVerification.utils';
import {
  hasPendingPhotoEvidenceUpload,
  resolveSyncedEvidenceUploadState,
} from '@/utils/locationVerificationStart.utils';
import { NavigateFunction } from 'react-router';
import { ROUTES } from '@/route/routes.constants';

type CompleteVerificationStartParams = {
  inspection: Inspection;
  draft: LocationVerificationDraft;
  inspectionId: string;
  userId: string | undefined;
  isOnline: boolean;
  isAdministrator: boolean;
  navigate: NavigateFunction;
};

function triggerLocationEvidenceUpload(inspection: Inspection, inspectionId: string): void {
  startLocationEvidenceUpload(inspectionId, inspection.location?.code);
}

export async function completeVerificationAndStart(
  params: CompleteVerificationStartParams
): Promise<boolean> {
  const {
    inspection,
    draft: inputDraft,
    inspectionId,
    userId,
    isOnline,
    isAdministrator,
    navigate,
  } = params;
  const draft = inputDraft;
  const confirmedAt = new Date().toISOString();
  const locationEvidence = toServerLocationEvidence(draft.evidence);
  const entranceCoords = getPrimaryEntranceCoordinates(draft);
  logFactoryVerificationDebug('start.putEvidence', {
    inspectionId,
    path: isOnline ? (isAdministrator ? 'admin' : 'online') : 'offline',
    locationCode: inspection.location?.code,
    evidence: summarizeLocationEvidence(draft.evidence),
    payloadEvidence: locationEvidence.map((item) => ({
      clientEvidenceId: item.clientEvidenceId,
      role: item.role,
      hasImageUrl: Boolean(item.imageUrl),
    })),
  });
  const startedDraft: LocationVerificationDraft = {
    ...draft,
    workflowState: 'STARTED',
    updatedAt: new Date().toISOString(),
  };
  await locationVerificationDraftStorage.put(startedDraft);
  const inspectionToSave: Inspection = {
    ...inspection,
    locationEvidence: draft.evidence,
    ...(entranceCoords
      ? { latitude: entranceCoords.latitude, longitude: entranceCoords.longitude }
      : {}),
  };
  if (!isOnline) {
    const inspectionStatusId = getStatusForStart(
      inspection.inspectionStatusId ?? InspectionStatus.PLANNED
    );
    const updatedInspection: Inspection = withWriteOnceStartTime(
      {
        ...inspectionToSave,
        inspectionStatusId,
        inspectedBy: userId,
      },
      isAdministrator,
      confirmedAt
    );
    delete updatedInspection.endTime;
    store.dispatch(upsertInspection(updatedInspection));
    navigate(ROUTES.INSPECTION_DETAILS.replace(':id', inspectionId), {
      state: { skipStartWorkFlow: true },
    });
    logFactoryVerificationDebug('start.triggerBackgroundUpload', {
      inspectionId,
      reason: 'offline',
      locationCode: inspection.location?.code,
    });
    triggerLocationEvidenceUpload(inspection, inspectionId);
    return true;
  }
  if (isAdministrator) {
    store.dispatch(upsertInspection(inspectionToSave));
    navigate(ROUTES.INSPECTION_DETAILS.replace(':id', inspectionId), {
      state: { skipStartWorkFlow: true },
    });
    logFactoryVerificationDebug('start.triggerBackgroundUpload', {
      inspectionId,
      reason: 'administrator',
      locationCode: inspection.location?.code,
    });
    triggerLocationEvidenceUpload(inspection, inspectionId);
    return true;
  }
  const inspectionStatusId = getStatusForStart(
    inspection.inspectionStatusId ?? InspectionStatus.PLANNED
  );
  const updateBody: {
    inspectionStatusId: string;
    inspectedBy: string | undefined;
    latitude?: number;
    longitude?: number;
    startTime?: string;
    locationEvidence: ReturnType<typeof toServerLocationEvidence>;
  } = {
    inspectionStatusId,
    inspectedBy: userId,
    locationEvidence,
  };
  if (!inspection.startTime) {
    updateBody.startTime = resolveWriteOnceStartTime(inspection.startTime, confirmedAt);
  }
  if (entranceCoords) {
    updateBody.latitude = entranceCoords.latitude;
    updateBody.longitude = entranceCoords.longitude;
  } else if ((inspection.latitude == null || inspection.longitude == null) && draft.evidence[0]) {
    const firstEvidence = draft.evidence[0];
    if (firstEvidence.latitude != null && firstEvidence.longitude != null) {
      updateBody.latitude = firstEvidence.latitude;
      updateBody.longitude = firstEvidence.longitude;
    }
  }
  const inspectionUrl = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.INSPECTIONS}/${inspectionId}`;
  const updateResponse = await clivApiRequest<Inspection>({
    url: inspectionUrl,
    method: 'PUT',
    body: updateBody,
  });
  if (!updateResponse.ok) {
    logFactoryVerificationError('start.putFailed', {
      inspectionId,
      status: updateResponse.status,
      requestId: updateResponse.requestId,
    });
    const revertedDraft: LocationVerificationDraft = {
      ...draft,
      workflowState: 'ROOM_CAPTURED',
      updatedAt: new Date().toISOString(),
    };
    await locationVerificationDraftStorage.put(revertedDraft);
    return false;
  }
  const syncedEvidence = draft.evidence.map((item) => ({
    ...item,
    uploadState: resolveSyncedEvidenceUploadState(item),
  }));
  logFactoryVerificationDebug('start.syncedEvidence', {
    inspectionId,
    evidence: summarizeLocationEvidence(syncedEvidence),
  });
  const syncedDraft: LocationVerificationDraft = {
    ...startedDraft,
    evidence: syncedEvidence,
    updatedAt: new Date().toISOString(),
  };
  await locationVerificationDraftStorage.put(syncedDraft);
  const updatedInspection: Inspection = withWriteOnceStartTime(
    {
      ...inspectionToSave,
      inspectionStatusId,
      inspectedBy: userId,
      locationEvidence: syncedEvidence,
    },
    false,
    confirmedAt
  );
  delete updatedInspection.endTime;
  store.dispatch(upsertInspection(updatedInspection));
  navigate(ROUTES.INSPECTION_DETAILS.replace(':id', inspectionId), {
    state: { skipStartWorkFlow: true },
  });
  const pendingPhotoUploads = hasPendingPhotoEvidenceUpload(syncedEvidence);
  logFactoryVerificationDebug('start.complete', {
    inspectionId,
    pendingPhotoUploads,
    draft: summarizeDraft(syncedDraft),
  });
  if (pendingPhotoUploads) {
    logFactoryVerificationDebug('start.triggerBackgroundUpload', {
      inspectionId,
      reason: 'pendingPhotoUploads',
      locationCode: inspection.location?.code,
    });
    triggerLocationEvidenceUpload(inspection, inspectionId);
  }
  return true;
}

type ApplyAutoReusedEvidenceParams = {
  inspection: Inspection;
  inspectionId: string;
  session: FactoryVisitSession;
  userId: string | undefined;
  tenantId: string;
  isOnline: boolean;
  isAdministrator: boolean;
  navigate: NavigateFunction;
};

export async function applyAutoReusedEvidenceAndStart(
  params: ApplyAutoReusedEvidenceParams
): Promise<boolean> {
  const { inspection, inspectionId, session, userId, tenantId } = params;
  const baseDraft: LocationVerificationDraft = {
    inspectionId,
    tenantId,
    inspectorId: userId || '',
    factoryId: inspection.factory?.id,
    workflowState: 'ENTRANCE_CAPTURED',
    evidence: [],
    observedStatusId: inspection.inspectionStatusId,
    updatedAt: new Date().toISOString(),
  };
  let nextDraft = buildAutoReuseDraft({ draft: baseDraft, session });
  for (const evidence of nextDraft.evidence.filter((item) => item.isReused)) {
    const sessionImage = await factoryVisitSessionStorage.getSessionImageData(
      session.factoryId,
      evidence.localImageKey
    );
    if (sessionImage) {
      await locationVerificationDraftStorage.saveEvidenceCapture({
        draft: nextDraft,
        evidence,
        imageData: sessionImage,
      });
    }
  }
  await locationVerificationDraftStorage.put(nextDraft);
  return completeVerificationAndStart({
    inspection,
    draft: nextDraft,
    inspectionId,
    userId,
    isOnline: params.isOnline,
    isAdministrator: params.isAdministrator,
    navigate: params.navigate,
  });
}

export function formatGpsCapturedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
